/**
 * Unified Icon System
 *
 * Combines Phosphor Icons with custom pet-themed icons
 * for a consistent icon API across the app.
 */

import {
  House,
  Users,
  ClipboardText,
  BookOpen,
  Gear,
  Plus,
  MagnifyingGlass,
  X,
  CaretRight,
  CaretLeft,
  CaretDown,
  CaretUp,
  Check,
  CheckCircle,
  Warning,
  Info,
  Star,
  Heart,
  Calendar,
  CalendarBlank,
  CalendarPlus,
  Clock,
  Lock,
  ChartBar,
  ChartLine,
  ChartPie,
  TrendUp,
  Trophy,
  Target,
  Flag,
  Bell,
  Camera,
  Image,
  Upload,
  Download,
  Trash,
  PencilSimple,
  Eye,
  EyeSlash,
  SignOut,
  User,
  Envelope,
  Phone,
  MapPin,
  List,
  GridFour,
  FunnelSimple,
  SortAscending,
  SortDescending,
  DotsThree,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowsClockwise,
  ArrowDown,
  Play,
  Pause,
  Stop,
  SkipForward,
  SkipBack,
  Confetti,
  Medal,
  Lightning,
  Sun,
  Moon,
  Palette,
  CircleNotch,
  GameController,
  Barbell,
  ChatCircle,
  Copy,
} from '@phosphor-icons/react';

import {
  DogFace,
  DogSilhouette,
  PawPrint,
  Bone,
  Leash,
  Collar,
  Training,
  ProgressPaw,
  Ball,
  TrickStar,
  TrophyPaw,
  Treat,
} from './PetIcons';

// Re-export Phosphor icons with consistent naming
export const Icons = {
  // Navigation
  Home: House,
  Customers: Users,
  Plans: ClipboardText,
  ClipboardText: ClipboardText,
  Content: BookOpen,
  Settings: Gear,

  // Actions
  Add: Plus,
  Plus: Plus,
  Search: MagnifyingGlass,
  Close: X,
  Edit: PencilSimple,
  Delete: Trash,
  View: Eye,
  Hide: EyeSlash,
  Upload: Upload,
  Download: Download,
  Camera: Camera,
  Image: Image,

  // Navigation arrows
  ChevronRight: CaretRight,
  ChevronLeft: CaretLeft,
  ChevronDown: CaretDown,
  ChevronUp: CaretUp,
  CaretRight: CaretRight,
  CaretLeft: CaretLeft,
  CaretDown: CaretDown,
  CaretUp: CaretUp,
  ArrowLeft: ArrowLeft,
  ArrowRight: ArrowRight,
  ArrowUp: ArrowUp,
  ArrowDown: ArrowDown,
  ArrowsClockwise: ArrowsClockwise,

  // Status
  Check: Check,
  CheckCircle: CheckCircle,
  Warning: Warning,
  Info: Info,
  Star: Star,
  Heart: Heart,

  // Time & Calendar
  Calendar: Calendar,
  CalendarBlank: CalendarBlank,
  CalendarPlus: CalendarPlus,
  Clock: Clock,
  Lock: Lock,
  Copy: Copy,

  // Charts
  ChartBar: ChartBar,
  ChartLine: ChartLine,
  ChartPie: ChartPie,
  TrendUp: TrendUp,

  // Activity
  GameController: GameController,
  Barbell: Barbell,
  ChatCircle: ChatCircle,

  // Loading
  CircleNotch: CircleNotch,

  // Achievements
  Trophy: Trophy,
  Medal: Medal,
  Target: Target,
  Flag: Flag,
  Confetti: Confetti,
  Lightning: Lightning,

  // Communication
  Bell: Bell,
  Envelope: Envelope,
  Phone: Phone,

  // User
  User: User,
  SignOut: SignOut,
  MapPin: MapPin,

  // Layout
  List: List,
  Grid: GridFour,
  Filter: FunnelSimple,
  SortAsc: SortAscending,
  SortDesc: SortDescending,
  More: DotsThree,

  // Media
  Play: Play,
  Pause: Pause,
  Stop: Stop,
  Next: SkipForward,
  Previous: SkipBack,

  // Theme
  Sun: Sun,
  Moon: Moon,
  Palette: Palette,

  // ═══════════════════════════════════════════════════════════════
  // CUSTOM PET ICONS
  // ═══════════════════════════════════════════════════════════════
  Dog: DogFace,
  DogSilhouette: DogSilhouette,
  Paw: PawPrint,
  Bone: Bone,
  Leash: Leash,
  Collar: Collar,
  Training: Training,
  Progress: ProgressPaw,
  Ball: Ball,
  TrickStar: TrickStar,
  TrophyPaw: TrophyPaw,
  Treat: Treat,
};

// Icon weight type (matches Phosphor)
export type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';

// Common icon props
export interface IconProps {
  size?: number;
  weight?: IconWeight;
  color?: string;
  className?: string;
}

// Type for any icon component
export type IconComponent = typeof House;

// Helper to get icon by name
export function getIcon(name: keyof typeof Icons): IconComponent | undefined {
  return Icons[name] as IconComponent;
}

// Re-export pet icons individually for direct import
export {
  DogFace,
  DogSilhouette,
  PawPrint,
  Bone,
  Leash,
  Collar,
  Training,
  ProgressPaw,
  Ball,
  TrickStar,
  TrophyPaw,
  Treat,
};

export default Icons;
